import { useLocation, useNavigate } from "react-router";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  RiAccountBoxLine,
  RiAddBoxLine,
  RiArrowDropDownLine,
  RiBuildingLine,
  RiDashboardLine,
  RiProfileLine,
  RiSettings3Line,
  RiSuitcaseLine,
  RiLogoutCircleLine,
  RiBillLine,
} from "react-icons/ri";
import { ArrowLeft, Bell, Book, BookCopy, BookMarkedIcon, Briefcase, BriefcaseBusiness, ChartBarIncreasing, ChevronDown, ChevronUp, CircleDollarSign, Clock, Cloud, Contact2, ContactRound, FileCheck2, FileEditIcon, FileStack, Flame, Hash, Home, LayoutList, Menu, Rows4, Search, SearchIcon, Signature, SquareCheckBig, SquareLibrary, SquarePen, X } from "lucide-react";
import { ArrowBack, MoreHorizOutlined, ChangeCircleOutlined, CloudDoneOutlined, CloudOutlined, Create, CreateOutlined, Add, AddOutlined, AddCircleOutline, DocumentScannerOutlined, Money, PunchClockOutlined, MoreTimeOutlined, QueryBuilderOutlined, AssessmentRounded, AssessmentOutlined, PermContactCalendarRounded } from "@mui/icons-material";
import Logo from "../resources/logo.png";
import "../styles/NavCompact.css";
import { Theme } from "./Theme";
import { EnhancedSearch } from "./EnhancedSearch";
import { NotificationTray } from "./NotificationTray";
import { NotificationTrayCompact } from "./NotificationTrayCompact";
import { setCookie } from "../utils/cookies";
import { useDocumentGenerationPreferences } from './DocumentGenerationPreferences';
import { canNavigateTimeBilling, TIME_BILLING_PATH } from '../utils/timeBillingNavigation';

const NavItem = ({ icon, label, to, onClick, dropdownItems, theme, changeTheme, isDeeper, onNavItemHover, onNavItemClick, switchNavVariant, hintText, showHint, hintEnabled, onDismissHint, onHintHoverChange }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showDropdown, setShowDropdown] = useState(false);
    const [dropdownMaxHeight, setDropdownMaxHeight] = useState(null);
  const [isMobileDropdown, setIsMobileDropdown] = useState(() => window.innerWidth <= 768);
    const itemRef = useRef(null);
    const dropdownRef = useRef(null);
    
    // Filter out falsy dropdown items (undefined, null, false)
    const validDropdownItems = dropdownItems?.filter(item => item) || [];
    const hasDropdown = validDropdownItems.length > 0;
    
    const isActive = location.pathname === to || 
                     (hasDropdown && validDropdownItems.some(item => item.route && location.pathname === item.route)) ||
                     (to === "/firm-settings" && location.pathname.startsWith("/firm-settings")) ||
                     (to === "/reports" && location.pathname.startsWith("/reports/"));

    const handleClick = () => {
        // Notify parent that nav item was clicked
        if (onNavItemClick) {
            onNavItemClick();
        }
        
        if (hasDropdown) {
            // If has dropdown, navigate to main route on click
            if (to) {
                navigate(to);
            }
            return;
        }
        if (onClick) {
            onClick();
        }
        if (to) {
            navigate(to);
        }
    };

    const handleDropdownItemClick = (item, event) => {
        if (item.isTheme) {
            // Don't navigate or close dropdown, let Theme component handle it
            event?.stopPropagation();
            return;
        }
        if (item.isNavSwitch) {
            // Handle nav variant switch
            event?.stopPropagation();
            setShowDropdown(false);
            if (item.action) {
                item.action();
            }
            return;
        }
        setShowDropdown(false);
        if (item.route) {
            navigate(item.route);
        }
    };

    const handleMouseEnter = () => {
      if (hasDropdown && onNavItemHover) {
        onNavItemHover(true);
      }
      if (hasDropdown) {
        if (hintEnabled && onHintHoverChange) {
          onHintHoverChange(true);
        }
        setShowDropdown(true);
      }
    };

    const handleMouseLeave = () => {
      if (hasDropdown) {
        // Add a small delay before closing to allow moving to dropdown
        setTimeout(() => {
          // Check if mouse is over the dropdown or nav item
          const dropdownElement = dropdownRef.current;
          const navItemElement = itemRef.current;
                
          if (dropdownElement && dropdownElement.matches(':hover')) {
            return; // Don't close if hovering dropdown
          }
          if (navItemElement && navItemElement.matches(':hover')) {
            return; // Don't close if still hovering nav item
          }
                
          setShowDropdown(false);
          if (hintEnabled && onHintHoverChange) {
            onHintHoverChange(false);
          }
          if (onNavItemHover) {
            onNavItemHover(false);
          }
        }, 50);
        return;
      }
    };

    const handleDropdownMouseLeave = () => {
        // Add a small delay before closing
        setTimeout(() => {
            const dropdownElement = dropdownRef.current;
            const navItemElement = itemRef.current;
            
            if (dropdownElement && dropdownElement.matches(':hover')) {
                return; // Don't close if still hovering dropdown
            }
            if (navItemElement && navItemElement.matches(':hover')) {
                return; // Don't close if hovering nav item
            }
            
            setShowDropdown(false);
            if (hintEnabled && onHintHoverChange) {
              onHintHoverChange(false);
            }
            if (onNavItemHover) {
                onNavItemHover(false);
            }
        }, 50);
    };

      useEffect(() => {
        const handleResize = () => {
          setIsMobileDropdown(window.innerWidth <= 768);
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
      }, []);

    useEffect(() => {
        if (!showDropdown || !hasDropdown) {
            setDropdownMaxHeight(null);
            return;
        }

        const updateDropdownMaxHeight = () => {
            const navItemElement = itemRef.current;
            const dropdownElement = dropdownRef.current;
            if (!navItemElement || !dropdownElement) {
                return;
            }

            const viewportMargin = 12;
            const dropdownGap = 15;
            const itemRect = navItemElement.getBoundingClientRect();
            const availableHeight = Math.floor(itemRect.top - dropdownGap - viewportMargin);

            if (availableHeight <= 0) {
                setDropdownMaxHeight(null);
                return;
            }

            const naturalHeight = dropdownElement.scrollHeight;
            setDropdownMaxHeight(naturalHeight > availableHeight ? availableHeight : null);
        };

        updateDropdownMaxHeight();
        window.addEventListener('resize', updateDropdownMaxHeight);

        return () => {
            window.removeEventListener('resize', updateDropdownMaxHeight);
        };
    }, [showDropdown, hasDropdown, validDropdownItems.length]);

    const dropdown = showDropdown && hasDropdown ? (
      <div 
        ref={dropdownRef}
        className="nav-compact-dropdown app-navigation"
        style={!isMobileDropdown && dropdownMaxHeight ? { maxHeight: `${dropdownMaxHeight}px`, overflowY: 'auto' } : undefined}
        onMouseLeave={handleDropdownMouseLeave}
      >
        {validDropdownItems.map((item, index) => (
          item.isTheme ? (
            <div 
              key={index} 
              className="nav-compact-dropdown-item theme-container"
              onClick={(e) => e.stopPropagation()}
            >
              <Theme theme={theme} changeTheme={changeTheme} />
            </div>
          ) : item.isCustomComponent ? (
            <div 
              key={index} 
              className="nav-compact-dropdown-item custom-component"
              onClick={(e) => e.stopPropagation()}
            >
              {item.component}
            </div>
          ) : (
            <div 
              key={index} 
              className="nav-compact-dropdown-item"
              onClick={(e) => {
                e.stopPropagation();
                handleDropdownItemClick(item, e);
              }}
            >
              {item.Icon && <item.Icon size={22} />}
              <span>{item.label}</span>
            </div>
          )
        ))}
      </div>
    ) : null;

    return (
        <div 
            ref={itemRef}
            className={`nav-item ${isActive ? 'active' : ''} ${isDeeper ? 'deeper' : ''}`} 
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            title={label}
        >
            {icon}
        {showHint && hintText && (
          <div className="nav-item-hint" onClick={(e) => e.stopPropagation()}>
            <div className="nav-item-hint-content">
              <span>{hintText}</span>
              <button
                className="action alt"
                onClick={(e) => {
                  e.stopPropagation();
                  onDismissHint && onDismissHint();
                }}
              >
                Ok
              </button>
            </div>
          </div>
        )}
          {isMobileDropdown && dropdown ? createPortal(dropdown, document.body) : dropdown}
        </div>
    );
}

const NavCompact = ({ isOpen, toggleNav, theme, changeTheme, accessLevel, switchNavVariant, logout, notifications, fetchNotifications }) => {
    const {
      reviewBeforeGeneration,
      saving: savingDocumentPreference,
      setReviewBeforeGeneration,
    } = useDocumentGenerationPreferences();
    const navigate = useNavigate();
    const location = useLocation();
    const [canGoBack, setCanGoBack] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [searchTemporarilyHidden, setSearchTemporarilyHidden] = useState(false);
    const [navMinimized, setNavMinimized] = useState(false);
    const [isMobileNav, setIsMobileNav] = useState(() => window.innerWidth <= 768);
  const [navWidth, setNavWidth] = useState(null);
    const [showMinimizeHint, setShowMinimizeHint] = useState(false);
    const [hideMinimizeHint, setHideMinimizeHint] = useState(false);
    const [isNavItemHovered, setIsNavItemHovered] = useState(false);
    const searchRef = useRef(null);
  const navRef = useRef(null);
    const isCaseDetailPage = /^\/case\/\d+/.test(location.pathname);

    const handleNavItemHover = (isHovering) => {
      setIsNavItemHovered(isHovering);
        if (showSearch) {
            setSearchTemporarilyHidden(isHovering);
        }
    };

    const handleNavItemClick = () => {
        // Permanently close search when any nav item is clicked
        setShowSearch(false);
        setSearchTemporarilyHidden(false);
    };

    useEffect(() => {
        // Check if there's navigation history within the app
        // window.history.length > 1 means there's at least one page to go back to
        const hasHistory = window.history.length > 1;
        setCanGoBack(hasHistory);
        console.log('History check:', { length: window.history.length, state: window.history.state, canGoBack: hasHistory });
    }, [location]);

    useEffect(() => {
      const handleResize = () => {
        const nextIsMobile = window.innerWidth <= 768;
        setIsMobileNav(nextIsMobile);

        if (nextIsMobile) {
          setNavMinimized(false);
        }
      };

      handleResize();
      window.addEventListener('resize', handleResize);

      return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowSearch(false);
            }
        };

        if (showSearch) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showSearch]);

    useEffect(() => {
        // Auto-focus the search input when search panel opens
        if (showSearch && !searchTemporarilyHidden) {
            // Small delay to ensure the input is rendered
            const timer = setTimeout(() => {
                const searchInput = searchRef.current?.querySelector('input[type="text"]');
                if (searchInput) {
                    searchInput.focus();
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [showSearch, searchTemporarilyHidden]);

    useEffect(() => {
      if (!navMinimized) {
        switchNavVariant && switchNavVariant('nav');
      }
    }, [navMinimized, switchNavVariant]);

    useEffect(() => {
      const dismissed = localStorage.getItem('navcompact_hint_minimize_dismissed') === 'true';
      if (!dismissed) {
        setShowMinimizeHint(true);
      }
    }, []);

    useEffect(() => {
      if (navMinimized || !navRef.current) {
        return;
      }

      const updateWidth = () => {
        if (navRef.current) {
          setNavWidth(navRef.current.offsetWidth);
        }
      };

      updateWidth();

      const observer = new ResizeObserver(updateWidth);
      observer.observe(navRef.current);

      return () => observer.disconnect();
    }, [navMinimized]);

    const handleBack = () => {
        navigate(-1);
    };

    const handleSearchToggle = (event) => {
      event.stopPropagation();
      setSearchTemporarilyHidden(false);
      setShowSearch((prevShowSearch) => !prevShowSearch);
    };

    const toggleDocumentReview = () => {
      if (savingDocumentPreference) return;
      setReviewBeforeGeneration(!reviewBeforeGeneration).catch((error) => {
        console.error('Unable to save document review preference:', error);
      });
    };

    if (navMinimized && !isMobileNav) {
        return (
          <>
            <div
              className="nav-compact-mini app-navigation"
              style={{ width: navWidth ? `${navWidth}px` : 'auto' }}
              onClick={() => {
                setNavMinimized(false);
                switchNavVariant && switchNavVariant('nav');
              }}
              title="Expand navigation"
            >
              <ChevronUp size={16} />
            </div>
            <div style={{ display: 'none' }}>
              <Theme theme={theme} changeTheme={changeTheme} />
            </div>
          </>
        );
    }

    return (
        <>
          <div
            className="nav-compact app-navigation"
            ref={navRef}
          >
            {canGoBack && (
              <div 
                className="nav-item nav-back-button" 
                onClick={handleBack}
                title="Go Back"
              >
                <ArrowLeft size={20} />
              </div>
            )}
            <div
              className="nav-search-wrapper"
              ref={searchRef}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div 
                className={`nav-item ${showSearch ? 'active' : ''}`}
                onClick={handleSearchToggle}
                title="Search"
              >
                <SearchIcon size={20} />
              </div>
              {showSearch && (
                <div
                  className={`nav-search-panel ${searchTemporarilyHidden ? 'is-hidden' : ''}`}
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  <EnhancedSearch 
                    expanded={true} 
                    setExpanded={() => {}} 
                    initiallyActive={true}
                    modal={false} 
                    enablePinning
                    onChange={() => {
                      setShowSearch(false);
                      setSearchTemporarilyHidden(false);
                    }} 
                  />
                </div>
              )}
            </div>
            <div className="nav-divider"></div>
            <NavItem 
              icon={<Home size={20} />} 
              label="Dashboard" 
              to="/dashboard"
              theme={theme}
              changeTheme={changeTheme}
              onNavItemHover={handleNavItemHover}
              onNavItemClick={handleNavItemClick}
              dropdownItems={[
                canNavigateTimeBilling(accessLevel) && {
                  label: "Time & Billing",
                  route: TIME_BILLING_PATH,
                  Icon: Clock,
                },
                accessLevel === 'global admin' && {
                  label: "Weather Events",
                  route: "/dashboard/weather",
                  Icon: Cloud,
                }
              ]}
            />
            <NavItem 
              icon={<BriefcaseBusiness size={20} />} 
              label="Cases"
              onNavItemHover={handleNavItemHover}
              onNavItemClick={handleNavItemClick} 
              to="/cases"
              theme={theme}
              changeTheme={changeTheme}
              isDeeper={isCaseDetailPage}
              dropdownItems={[
                {
                  label: "Intake",
                  route: "/intake",
                  Icon: Signature,
                },
                {
                  label: "Contacts",
                  route: "/contacts",
                  Icon: ContactRound,
                },
              ]}
            />
            <NavItem 
              icon={<BookCopy size={20} />} 
              label="Library" 
              to="/library" 
              theme={theme} 
              changeTheme={changeTheme}
              onNavItemHover={handleNavItemHover}
              onNavItemClick={handleNavItemClick}
            />
            <NavItem 
              icon={<LayoutList size={20} />} 
              label="Reports" 
              to="/reports"
              theme={theme}
              changeTheme={changeTheme}
              onNavItemHover={handleNavItemHover}
              onNavItemClick={handleNavItemClick}
            />
            <div className="nav-divider"></div>
            {/*<NavItem 
              icon={<ReviewsOutlinedIcon sx={{ fontSize: 20 }} />} 
              label="AI" 
              to="/ai" 
              theme={theme} 
              changeTheme={changeTheme}
              onNavItemHover={handleNavItemHover}
              onNavItemClick={handleNavItemClick}
            />*/}
            {(accessLevel === 'global admin' || accessLevel === 'admin') && (
              <NavItem 
                icon={<RiBuildingLine size={20} />} 
                label="Firm Settings" 
                to="/firm-settings"
                theme={theme}
                changeTheme={changeTheme}
                onNavItemHover={handleNavItemHover}
                onNavItemClick={handleNavItemClick}
                dropdownItems={[
                  {
                    label: "Billing",
                    route: "/firm-settings/0",
                    Icon: CircleDollarSign,
                  },
                  {
                    label: "Billing Setup",
                    route: "/firm-settings/1",
                    Icon: RiBillLine,
                  },
                  {
                    label: "Custom Fields",
                    route: "/firm-settings/2",
                    Icon: FileEditIcon,
                  },
                  {
                    label: "Layout Editor",
                    route: "/firm-settings/3",
                    Icon: ChangeCircleOutlined,
                  },
                  {
                    label: "User Management",
                    route: "/firm-settings/4",
                    Icon: RiAccountBoxLine,
                  },
                  {
                    label: "Task Workflows",
                    route: "/firm-settings/5",
                    Icon: SquareCheckBig,
                  },
                  {
                    label: "Team Reassignment",
                    route: "/firm-settings/6",
                    Icon: ContactRound,
                  },
                ]}
              />
            )}
            <NavItem 
              icon={
                <>
                  <Bell size={20} />
                  {notifications?.filter(noti => noti.read_at === null)?.length > 0 && (
                    <div style={{ 
                      position: 'absolute', 
                      top: '-6px', 
                      right: '-6px',
                      fontSize: '10px',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 4px'
                    }} className='notification priority def'>
                      {notifications?.filter(noti => noti.read_at === null)?.length < 20 
                        ? notifications?.filter(noti => noti.read_at === null)?.length 
                        : '20'}
                    </div>
                  )}
                </>
              }
              label="Notifications" 
              to="/notifications"
              theme={theme}
              changeTheme={changeTheme}
              onNavItemHover={handleNavItemHover}
              onNavItemClick={handleNavItemClick}
              dropdownItems={[
                {
                  isCustomComponent: true,
                  component: <NotificationTrayCompact notifications={notifications?.filter(noti => noti.read_at === null) || []} fetchNotifications={fetchNotifications} />
                }
              ]}
            />
            <NavItem 
              icon={<RiSettings3Line size={20} />} 
              label="Settings" 
              to="/settings"
              theme={theme}
              changeTheme={changeTheme}
              onNavItemHover={handleNavItemHover}
              onNavItemClick={handleNavItemClick}
              switchNavVariant={switchNavVariant}
              dropdownItems={[
                {
                  label: `Document review: ${reviewBeforeGeneration ? 'On' : 'Off'}`,
                  route: null,
                  Icon: FileCheck2,
                  isNavSwitch: true,
                  action: toggleDocumentReview,
                },
                {
                  label: "Revert to Classic Nav",
                  route: null,
                  Icon: Menu,
                  isNavSwitch: true,
                  action: () => {
                    setCookie('nav-reverted-v2', 'true', 365);
                    switchNavVariant && switchNavVariant('full');
                  },
                },
                {
                  label: "Logout",
                  route: null,
                  Icon: RiLogoutCircleLine,
                  isNavSwitch: true,
                  action: () => logout && logout(),
                },
                {
                  label: "Theme",
                  route: null,
                  Icon: null,
                  isTheme: true,
                },
              ]}
            />
            <div
              className="nav-item nav-minimize"
              style={isMobileNav ? { display: 'none' } : undefined}
              onClick={() => {
                setShowSearch(false);
                setSearchTemporarilyHidden(false);
                switchNavVariant && switchNavVariant('compact');
                localStorage.setItem('navcompact_hint_minimize_dismissed', 'true');
                setShowMinimizeHint(false);
                setHideMinimizeHint(false);
                setNavMinimized(true);
              }}
              title="Minimize navigation"
            >
              <ChevronDown size={20} />
              {showMinimizeHint && !hideMinimizeHint && !isNavItemHovered && (
                <div className="nav-item-hint" onClick={(e) => e.stopPropagation()}>
                  <div className="nav-item-hint-content">
                    <span>The nav can now be minimized to show more of the page.</span>
                    <button
                      className="action alt"
                      onClick={(e) => {
                        e.stopPropagation();
                        localStorage.setItem('navcompact_hint_minimize_dismissed', 'true');
                        setShowMinimizeHint(false);
                        setHideMinimizeHint(false);
                      }}
                    >
                      Ok
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Hidden Theme component to ensure it's always in the DOM */}
          <div style={{ display: 'none' }}>
            <Theme theme={theme} changeTheme={changeTheme} />
          </div>
        </>
    );
}

export default NavCompact;
